import { DeleteRestrictionMutationVariables } from "@/types/gql/graphql";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Restriction } from "@/types";

import { Button } from "@/components/ui/button";
import { TimeSpanItem } from "@/components/time-span/time-span-item";
import { distanceUnit } from "@/constants/mapbox.const";
import { getRestrictionTypeName, numberFormat } from "@/helper/format-data";
import { useMemo, Dispatch, SetStateAction } from "react";
import useToggle from "@/hooks/use-toggle";

type RestrictionDetailProps = {
  id: string;
  restriction: Restriction;
  deleteRestriction: (input: DeleteRestrictionMutationVariables) => void;
  openRestrictionModal: Dispatch<SetStateAction<boolean>>;
};

export const RestrictionDetail = ({
  id,
  deleteRestriction,
  restriction,
  openRestrictionModal,
}: RestrictionDetailProps) => {
  const [openConfirm, setOpenConfirm] = useToggle();

  const decreeFileNames = useMemo(() => {
    if (restriction?.decrees?.length) {
      return restriction?.decrees?.map((item) => item.name);
    }
    return [];
  }, [restriction]);

  const removeParking = () => {
    deleteRestriction({
      id,
    });
  };

  const confirmText = restriction.curbZoneId ? "Curb zone restriction" : "Street restriction";

  return (
    <div className="transition-opacity absolute h-auto top-36 bg-primary p-3 right-6 mt-2 mb-3 rounded-md shadow-lg z-5 min-w-80 max-w-128">
      <div className="flex flex-col gap-4 text-xs text-secondary">
        <div className="flex justify-between w-full items-center gap-2">
          <span>TimeSpan</span>
          <div className="flex flex-col ml-4 max-h-100 overflow-y-auto">
            {restriction.timeSpans?.map((timeSpan, timeSpanIndex) => (
              <div className="flex items-center" key={`time-span-${timeSpanIndex}`}>
                <TimeSpanItem
                  timeSpan={timeSpan}
                  name={`timeSpans[${timeSpanIndex}]`}
                  options={{
                    showSelectDate: true,
                  }}
                  readonly
                  className="text-secondary"
                />
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-between w-full items-center gap-2">
          <span>Description</span>
          <span>{restriction?.description}</span>
        </div>
        <div className="flex justify-between w-full items-center gap-2">
          <span>Decree</span>
          <span>
            <div className="flex flex-col gap-2">
              {decreeFileNames.map((item, idx) => (
                <span key={`detail-decree-${idx}`}>{item}</span>
              ))}
            </div>
          </span>
        </div>
        <div className="flex justify-between w-full items-center gap-2">
          <span>Type</span>
          {restriction.type && <span>{getRestrictionTypeName(restriction.type)}</span>}
        </div>
        <div className="flex justify-between w-full items-center gap-2">
          <span>Longueur</span>
          {restriction.length && <span>{`${numberFormat(restriction.length)} ${distanceUnit}`}</span>}
        </div>
        <div className="flex justify-between w-full items-center gap-2">
          <span>Places calculées</span>
          {restriction.calculatedSpaces && <span>{restriction.calculatedSpaces}</span>}
        </div>
      </div>
      <div className="flex justify-between mt-4">
        <Button onClick={setOpenConfirm} variant="destructive">
          Delete
        </Button>
        <Button onClick={() => openRestrictionModal(true)} variant="secondary">
          Edit
        </Button>
      </div>
      <AlertDialog open={openConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{`Delete ${confirmText}`}</AlertDialogTitle>
            <AlertDialogDescription>{`Are you sure that you want to delete this ${confirmText}?`}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={setOpenConfirm}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removeParking}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
